import React, { useEffect, useMemo, useState } from 'react';
import { useStores, invalidateStore } from '../hooks/useDB';
import PageLoader from '../components/PageLoader';
import { GraduationCap, RefreshCw, LayoutList, UserCircle, DatabaseZap } from 'lucide-react';
import OnboardingAdmin from './OnboardingAdmin';
import OnboardingEmployee from './OnboardingEmployee';

const OB_STORES = ['onboarding', 'onboarding_tasks', 'onboarding_logs', 'onboarding_docs', 'attendees'];

// Entry point for the Engineer Onboarding app. Loads the shared stores once and
// picks the right face of the product for whoever is signed in:
//   • admins            → the full console across every engineer
//   • assigned mentors  → the same console, scoped to their mentees
//   • the new engineer  → their own onboarding (tasks, log, documents to sign)
// Someone can be both (a mentor who is also new), so a switch appears for them.
export default function OnboardingApp({ currentUser = {}, isAdmin = false }) {
  const stores = useStores(OB_STORES);
  const { attendees, loading } = stores;
  const journeys = stores.onboarding;
  const tasks = stores.onboarding_tasks;
  const logs = stores.onboarding_logs;
  const docs = stores.onboarding_docs;

  const me = (currentUser.email || '').toLowerCase();

  const myJourney = useMemo(
    () => journeys.find((j) => (j.engineerEmail || '').toLowerCase() === me) || null,
    [journeys, me]
  );
  const mentored = useMemo(
    () => journeys.filter((j) => (j.mentorEmail || '').toLowerCase() === me),
    [journeys, me]
  );

  const isReviewer = isAdmin || mentored.length > 0;
  const [view, setView] = useState('auto');
  const showingConsole = view === 'console' || (view === 'auto' && isReviewer);

  const refresh = () => OB_STORES.forEach(invalidateStore);

  // If a store fails to load (most likely because the onboarding tables have
  // not been created yet) the data layer leaves it un-cached so the next access
  // retries — which would otherwise leave this page spinning forever. Surface
  // an explanation instead once it's clearly not just a slow network.
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    if (!loading) { setStalled(false); return undefined; }
    const t = setTimeout(() => setStalled(true), 8000);
    return () => clearTimeout(t);
  }, [loading]);

  if (loading && stalled) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm max-w-xl mx-auto">
        <DatabaseZap className="mx-auto text-amber-500 mb-3" size={36} />
        <h3 className="text-lg font-bold text-slate-800 font-display">Onboarding data isn't loading</h3>
        {isAdmin ? (
          <>
            <p className="text-sm text-slate-500 mt-2 text-left">
              This app needs two one-time setup steps. Open the browser console to see which one is missing:
              <span className="font-semibold"> “Unknown store”</span> means the API is still on an older build,
              and a <span className="font-semibold">500</span> means the tables aren't created yet.
            </p>
            <div className="mt-3 space-y-2 text-left">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">1 · Redeploy the API</div>
                <code className="block text-xs bg-slate-900 text-slate-100 rounded-lg px-3 py-2 font-mono overflow-x-auto">
                  gcloud builds submit --config api/cloudbuild.yaml .
                </code>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">2 · Create the tables (idempotent)</div>
                <code className="block text-xs bg-slate-900 text-slate-100 rounded-lg px-3 py-2 font-mono overflow-x-auto">
                  psql "$DB_URL" -f supabase.sql
                </code>
              </div>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-500 mt-2">
            Something went wrong fetching your onboarding. Please try again, or ask an admin to take a look.
          </p>
        )}
        <button
          onClick={refresh}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition"
        >
          <RefreshCw size={15} /> Try again
        </button>
      </div>
    );
  }

  if (loading) return <PageLoader label="Loading onboarding…" sublabel="Fetching plans, logs and documents" />;

  const nothingForMe = !isReviewer && !myJourney;

  return (
    <div className="space-y-4">
      {/* Role switch + refresh. The data layer caches per session, so a manual
          refresh is how a mentor picks up a log the engineer just added. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {isReviewer && myJourney ? (
          <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm inline-flex gap-1">
            <button
              onClick={() => setView('console')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${showingConsole ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <LayoutList size={14} /> {isAdmin ? 'All engineers' : 'My mentees'}
            </button>
            <button
              onClick={() => setView('mine')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${!showingConsole ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <UserCircle size={14} /> My onboarding
            </button>
          </div>
        ) : <span />}
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition shadow-sm"
          title="Refresh to see the latest updates"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {nothingForMe ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <GraduationCap className="mx-auto text-slate-300 mb-3" size={40} />
          <h3 className="text-lg font-bold text-slate-800 font-display">No onboarding assigned to you</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            This is where a new engineer follows their 15-day plan, logs daily progress and signs their documents.
            If you've just joined, an admin will set yours up shortly.
          </p>
        </div>
      ) : showingConsole ? (
        <OnboardingAdmin
          currentUser={{ ...currentUser, role: isAdmin ? 'admin' : 'mentor' }}
          isAdmin={isAdmin}
          journeys={isAdmin ? journeys : mentored}
          tasks={tasks}
          logs={logs}
          docs={docs}
          attendees={attendees}
        />
      ) : (
        <OnboardingEmployee
          currentUser={currentUser}
          journey={myJourney}
          tasks={tasks.filter((t) => t.onboardingId === myJourney.id)}
          logs={logs.filter((l) => l.onboardingId === myJourney.id)}
          docs={docs.filter((d) => d.onboardingId === myJourney.id)}
        />
      )}
    </div>
  );
}
